<template>
  <v-dialog v-model="dialog" width="520" @click:outside="cancel" :persistent="isLoading">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('tanaoroshiSessionDialog.action.' + dialogData.action) }}</strong></h3>
      </v-card-title>
      <v-card-text class="pre-line">
        <div class="mb-3">
          {{ $t('tanaoroshiSessionDialog.label.target', { id: dialogData.tanaoroshi.tanaoroshiId, status: dialogData.tanaoroshi.statusLabel }) }}
        </div>
        <!-- 「※」で始まる行は注意書きなので赤字にする（受入テスト #22。2026-11-01） -->
        <div class="mb-3">
          <div v-for="(line, i) in messageLines" :key="i" :class="{ warn: line.startsWith('※') }">{{ line }}</div>
        </div>

        <!-- 終了・確定の前に、送信が終わっていないリーダーを知らせる（FR-W-11） -->
        <div v-if="notDone > 0 && (dialogData.action === 'END' || dialogData.action === 'CONFIRM')" class="warn mb-3">
          {{ $t('tanaoroshiSessionDialog.message.notDone', { count: notDone }) }}
        </div>
        <!-- 確定の文言はお客様指定の文面（message.CONFIRM）だけにする。
             以前出していた赤字の注意書き（confirmIrreversible）は、指定の文面に付け足した形になるので外した（JCIKAIHATSU-514。2026-11-04） -->
        <!-- 確定の取り消しでは、確定後に届いた読取データを取り込む -->
        <div v-if="dialogData.action === 'CANCEL_CONFIRM' && (dialogData.tanaoroshi.lateEpcCount ?? 0) > 0" class="warn mb-3">
          {{ $t('tanaoroshiSessionDialog.message.lateImport', { count: dialogData.tanaoroshi.lateEpcCount }) }}
        </div>

        <!-- 削除の前に、消える棚卸にぶら下がっている件数を見せて誤操作を防ぐ（サーバー修正 S-10 ①）。
             一覧の検索（ページ指定）は重いので読取件数を返さない。開いたときに参加リーダー一覧から数える。
             読取中のリーダーは削除の合図で読取を止める（S-10 ②。アプリ側は修正箇所一覧 No.25） -->
        <div v-if="dialogData.action === 'DELETE'" class="warn mb-3">
          <template v-if="deleteSummary">
            {{ $t('tanaoroshiSessionDialog.message.deleteCounts', {
              epc: deleteSummary.epc.toLocaleString(), device: deleteSummary.device }) }}
            <template v-if="deleteSummary.active > 0">
              <br>{{ $t('tanaoroshiSessionDialog.message.deleteActive', { count: deleteSummary.active }) }}
            </template>
          </template>
          <template v-else-if="isCounting">{{ $t('tanaoroshiSessionDialog.message.deleteCounting') }}</template>
        </div>

        <div v-if="errorMessage" class="error mt-2">{{ errorMessage }}</div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">{{ $t('common.action.cancel') }}</v-btn>
        <v-btn
          :color="['CONFIRM', 'END', 'CANCEL_CONFIRM', 'DELETE'].includes(dialogData.action) ? 'red-darken-2' : 'indigo-darken-1'"
          dark
          :loading="isLoading"
          :disabled="isCounting"
          @click="ok"
        >{{ $t('tanaoroshiSessionDialog.action.' + dialogData.action) }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import constant from "@/utils/constant";
import utils from "@/utils";

/**
 * 棚卸の状態の操作（棚卸終了・終了取消・確定・確定解除・削除。一時中断・再開は 2026-10-26 に廃止）の確認ダイアログ
 */
export default {
  props: {
    // { action: "SAVE" など, tanaoroshi: 一覧の1行 }
    dialogData: { type: Object, required: true },
    handleOk: { type: Function, required: true },
    handleCancel: { type: Function, required: true },
  },

  computed: {
    // 操作の説明を行ごとに分ける（行頭の「※」で強調するため）
    messageLines() {
      return this.$t('tanaoroshiSessionDialog.message.' + this.dialogData.action).split("\n");
    },
    notDone() {
      return this.dialogData.tanaoroshi.notDoneReaderCount ?? this.dialogData.tanaoroshi.activeDeviceCount ?? 0;
    },
  },

  data() {
    return {
      dialog: true,
      isLoading: false,
      errorMessage: "",
      // 削除の確認に出す件数 { epc: 数量に入った読取データ, device: 参加リーダー, active: 読取中のリーダー }
      deleteSummary: null,
      isCounting: false,
    };
  },

  mounted() {
    if (this.dialogData.action === "DELETE") this.countForDelete();
  },

  methods: {
    // 数え終わるまで「削除」は押せない。数えられなかったときは件数を出さずに押せるようにする（削除自体は止めない）
    countForDelete() {
      this.isCounting = true;
      api.post("/tanaoroshi/readers", { tanaoroshiId: this.dialogData.tanaoroshi.tanaoroshiId })
        .then((response) => {
          if (response.data.status != constant.RESPONSE_STATUS.SUCCESS) return;
          const r = response.data.results || {};
          const readers = r.readerList || [];
          this.deleteSummary = {
            epc: readers.reduce((sum, e) => sum + (e.readCount || 0), 0),
            device: r.deviceCount ?? readers.length,
            active: r.activeDeviceCount ?? 0,
          };
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isCounting = false;
        });
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    ok() {
      this.isLoading = true;
      this.errorMessage = "";
      api.post("/tanaoroshi/session/operate", {
        tanaoroshiId: this.dialogData.tanaoroshi.tanaoroshiId,
        action: this.dialogData.action,
      })
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            this.handleOk(this.dialogData.action);
            return;
          }
          this.errorMessage = response.data.errorMessage;
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.pre-line {
  white-space: pre-line;
}
.warn {
  color: #c62828;
  font-weight: 700;
}
.error {
  color: #c62828;
}
</style>
